<template>
  <div>
    <b-card>
      <div class="prod-toolbar">
        <div>
          <h3 class="mb-25">
            Production
          </h3>
          <div class="text-muted">
            Choose a farmer to view all their farms and farm records.
          </div>
        </div>
        <b-form-input
          v-model="search"
          placeholder="Search farmers..."
          class="prod-toolbar__search"
          @keyup.enter="load(1)"
        />
      </div>

      <div
        v-if="loading"
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!rows.length"
        class="prod-empty text-center py-5"
      >
        <div class="prod-empty__icon">
          🌾
        </div>
        <h4 class="mb-50">
          No farmers found
        </h4>
        <p class="text-muted mb-0">
          {{ search ? 'No farmers match your search.' : 'Register members to see production records.' }}
        </p>
      </div>

      <b-row v-else>
        <b-col
          v-for="farmer in rows"
          :key="farmer.id"
          md="4"
          class="mb-2"
        >
          <article
            class="prod-card h-100 cursor-pointer"
            @click="openFarmer(farmer)"
          >
            <div class="prod-card__avatar">
              {{ initials(farmer.full_name) }}
            </div>
            <h4 class="prod-card__title">
              {{ farmer.full_name }}
            </h4>
            <div class="prod-card__meta">
              {{ farmer.community || farmer.member_id || '—' }}
            </div>

            <div class="prod-card__stats">
              <div class="prod-card__stat">
                <small>Farms</small>
                <b>{{ farmer.farms_count || 0 }}</b>
              </div>
              <div class="prod-card__stat">
                <small>Total farm size</small>
                <b>{{ formatSize(farmer.total_size) }} ha</b>
              </div>
            </div>

            <button
              type="button"
              class="prod-card__cta"
              @click.stop="openFarmer(farmer)"
            >
              View farmer farms
            </button>
          </article>
        </b-col>
      </b-row>

      <div
        v-if="total > perPage"
        class="d-flex justify-content-end mt-2"
      >
        <b-pagination
          v-model="page"
          :total-rows="total"
          :per-page="perPage"
          @change="onPageChange"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BPagination, BSpinner,
} from 'bootstrap-vue'

export default {
  name: 'ProductionIndex',
  components: {
    BCard, BRow, BCol, BFormInput, BPagination, BSpinner,
  },
  data() {
    return {
      search: '',
      page: 1,
      perPage: 12,
      loading: false,
    }
  },
  computed: {
    rows() {
      return this.$store.getters['farmManager/productionRows']
    },
    total() {
      return this.$store.getters['farmManager/productionTotal']
    },
  },
  mounted() {
    this.load(1)
  },
  methods: {
    load(page = 1) {
      this.page = page
      this.loading = true
      this.$store.dispatch('farmManager/fetchProduction', {
        search: this.search,
        page: this.page,
        per_page: this.perPage,
      }).finally(() => { this.loading = false })
    },
    onPageChange(page) {
      this.load(page)
    },
    openFarmer(row) {
      this.$router.push({ name: 'production-farmer-farms', params: { memberId: row.id } })
    },
    initials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('')
    },
    formatSize(value) {
      const num = Number(value || 0)
      return Number.isInteger(num) ? String(num) : num.toFixed(1)
    },
  },
}
</script>

<style scoped>
.prod-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.prod-toolbar__search {
  width: 100%;
  max-width: 260px;
  min-width: 180px;
}

.prod-empty {
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.02);
}

.prod-empty__icon {
  font-size: 2rem;
  margin-bottom: 0.35rem;
}

.cursor-pointer {
  cursor: pointer;
}

.prod-card {
  display: flex;
  flex-direction: column;
  padding: 1.15rem;
  border: 1px solid #e4ebe6;
  border-radius: 1rem;
  background: linear-gradient(180deg, #f7fbf8 0%, #ffffff 42%);
  box-shadow: 0 1px 2px rgba(4, 196, 49, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.prod-card:hover {
  transform: translateY(-2px);
  border-color: #c9ddd2;
  box-shadow: 0 0.5rem 1.25rem rgba(4, 196, 49, 0.1);
}

.prod-card__avatar {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: #04c431;
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
}

.prod-card__title {
  margin: 0.9rem 0 0.3rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #04c431;
}

.prod-card__meta {
  color: #6b7c72;
  font-size: 0.9rem;
  margin-bottom: 0.9rem;
}

.prod-card__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.prod-card__stat {
  background: #f6f8f6;
  border-radius: 0.7rem;
  padding: 0.65rem 0.75rem;
}

.prod-card__stat small {
  display: block;
  color: #7a8a80;
  font-size: 0.72rem;
  margin-bottom: 0.15rem;
}

.prod-card__stat b {
  display: block;
  color: #04c431;
  font-size: 0.95rem;
  font-weight: 700;
}

.prod-card__cta {
  margin-top: 0.95rem;
  width: 100%;
  border: 0;
  border-radius: 0.75rem;
  padding: 0.7rem 0.9rem;
  background: #04c431;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
}

.prod-card__cta:hover {
  background: #039a28;
}

@media (max-width: 576px) {
  .prod-toolbar__search {
    max-width: none;
  }
}
</style>
